import * as React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ClassicLayerCompat, LayerContext, useLayer, getZIndex, RESERVED_MODAL_LAYER, getPortalNodeForTesting } from '@sail/global-state';

/**
 * As of React 16, first-class portals bubble events up to parent components.
 * This can lead to unexpected behavior, especially where parent components
 * have event listeners that trigger layers to open or close. For instance, a
 * click on an element might open a flyout, but a click on a "Close" button
 * within that flyout could bubble up to the parent element, causing it to stay
 * open.
 *
 * It's easier to think of portals as being the top level for all event
 * bubbling for events occurring within the portal, since that's conceptually
 * similar to expected DOM behavior. To that end, we stop propagation on
 * bubbling events here.
 *
 * The list of handlers defined in `render` is based on
 * https://en.wikipedia.org/wiki/DOM_events#Events. It is not exhaustive since
 * callsites don't generally use the full set of events, but it aims to
 * cover some of the more common and obvious potential problem cases.
 */
function handleStop(e) {
  e.stopPropagation();
}
export const NextLayer = /*#__PURE__*/React.forwardRef(({
  name,
  children
}, ref) => {
  const parentContext = useContext(LayerContext);
  if (parentContext == null && process.env.NODE_ENV !== 'test') {
    throw new Error('Parent layer not available; did you render a <RootLayer /> around your app?');
  }

  // `type` is deprecated but not fully removed from the APIs yet so
  // unfortunately we still need to pass it in
  const type = name === RESERVED_MODAL_LAYER ? 'overlay' : 'partial';
  useEffect(() => {
    parentContext && parentContext.addLayer(type);
    return () => {
      parentContext && parentContext.removeLayer(type);
    };
  }, [parentContext, type]);
  const context = useLayer({
    type,
    name
  });
  const zIndex = useMemo(() => getZIndex(context), [context]);
  let portalNode = context.container;
  if (process.env.NODE_ENV === 'test') {
    portalNode = getPortalNodeForTesting(context, portalNode);
  }
  if (portalNode == null) {
    return null;
  }
  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(LayerContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(ClassicLayerCompat, {
    onMouseDown: handleStop,
    onMouseUp: handleStop,
    onChange: handleStop,
    onClick: handleStop,
    onKeyDown: handleStop,
    onKeyPress: handleStop,
    onKeyUp: handleStop,
    onSubmit: handleStop,
    ref: ref,
    style: name === RESERVED_MODAL_LAYER ? {
      position: 'fixed',
      inset: 0,
      zIndex,
      contain: 'strict',
      pointerEvents: context.hasOverlay() ? 'none' : 'auto'
    } : {
      position: 'absolute',
      zIndex,
      pointerEvents: context.hasOverlay() ? 'none' : 'auto'
    }
  }, children)), portalNode);
});